import type { SwappedInboundMessage } from '@funkit/connect-core'
import { useCallback } from 'react'

import { logger } from '../logging/logger'
import {
  type SwappedScreenBaseProps,
  SwappedScreenShell,
} from './SwappedScreenShell'

export interface SwappedScreenProps extends SwappedScreenBaseProps {
  lockAmount?: boolean
  /** Fired when the embed reports a successful transaction. */
  onSuccess?: () => void
}

/**
 * Deposit-mode host for the Swapped embed (mirroring web's
 * `SwappedIframeController`): back / new-deposit → {@link onBack},
 * close → {@link onClose}, transaction-successful → {@link onSuccess}.
 * Withdrawal lifecycle messages are ignored — see `SwappedWithdrawScreen`.
 */
export function SwappedScreen({
  url,
  onClose,
  onBack,
  onSuccess,
  lockAmount,
}: SwappedScreenProps) {
  const handleMessage = useCallback(
    (msg: SwappedInboundMessage) => {
      switch (msg.type) {
        // Same destination, logged apart: "went back" and "started another
        // deposit" are different intents (web parity).
        case 'SWAPPED_GO_BACK':
          logger.info('connect-rn:swapped:back')
          onBack()
          break
        case 'SWAPPED_NEW_DEPOSIT':
          logger.info('connect-rn:swapped:back_and_new_deposit')
          onBack()
          break
        case 'SWAPPED_CLOSE':
          logger.info('connect-rn:swapped:exit')
          onClose()
          break
        case 'SWAPPED_TRANSACTION_SUCCESSFUL':
          logger.info('connect-rn:swapped:transaction_successful')
          onSuccess?.()
          break
        default:
          // READY / NAVIGATION / LOGOUT / ERROR handled in the shell or
          // tracked elsewhere; withdrawal lifecycle messages belong to
          // SwappedWithdrawScreen.
          break
      }
    },
    [onBack, onClose, onSuccess],
  )

  return (
    <SwappedScreenShell
      url={url}
      onMessage={handleMessage}
      onBack={onBack}
      lockAmount={lockAmount}
    />
  )
}
